import React from 'react';
import { useTranslation } from 'react-i18next';
import { PermissionType } from 'Services/permissions/interfaces';
import { permissionDict as p } from 'Services/permissions/permission-dict';
import { hasRequiredPermissions } from 'Services/permissions';
import { MenuItem } from 'App/menu-items';
import { useAppUser } from './AppUserProvider';
import { UI_VIEW_PERMISSIONS } from 'App/components/config/constants';

export type ProductIdType = 'eis' | 'pb' | 'pbm';

export const eisPID: ProductIdType = 'eis';
export const pbPID: ProductIdType = 'pb';
export const pbmID: ProductIdType = 'pbm';

/**
 * Interface that describe product
 */
export interface IAppProductDescriptor {
  /**
   * Id that can identify product
   */
  readonly id: ProductIdType;
  /**
   * Name of the product displayed in selector and next to the emil logo in top left corner
   */
  readonly name: string;
  /**
   * This is needed so we know how to limit user permissions based on current product.
   */
  readonly limitPermissions?: PermissionType[];
  menuItems: MenuItem[];
}

/**
 * It provides all products descriptors supported by this application.
 */
export const AppProductsProvider: React.FC = ( props ) => {
  const { t } = useTranslation( [ 'base' ] );
  const products = React.useMemo<IAppProductDescriptor[]>( () => {
    const eis: IAppProductDescriptor = {
      id: eisPID,
      name: 'EMIL Insurance Suite',
      menuItems: [
        {
          id: 'home',
          title: t( 'menu.item.home' ),
          type: 'item',
          icon: 'feather icon-home',
          url: '/dashboard',
          breadcrumbs: false,
          requiredPermissions: [
            [ p.eis_policy_view ],
            [ p.eis_products_view ],
            [ p.policy_management_policies_view ],
            [ p.policy_management_products_view ],
          ],
        },
        {
          id: 'partnerManagement',
          title: t( 'menu.item.partnerManagement' ),
          type: 'collapse',
          icon: 'feather icon-users',
          url: '/',
          breadcrumbs: true,
          requiredPermissions: [
            [ p.eis_policy_view ],
            [ p.partner_management_all ],
            [ p.partner_management_partners_all ],
            [ p.partner_management_partners_view ],
            [ p.partner_management_partners_create ],
            [ p.partner_management_partners_update ],
            [ p.partner_management_partners_delete ],
            [ p.commission_management_all ],
            [ p.commission_management_agreements_view ],
            [ p.commission_management_agreements_create ],
            [ p.commission_management_agreements_update ],
            [ p.commission_management_agreements_delete ],
          ],
          children: [
            {
              id: 'partners',
              title: t( 'menu.item.partnerOverview' ),
              type: 'item',
              url: '/partners/all',
              breadcrumbs: false,
              nestedApplication: true,
              requiredPermissions: [
                [ p.eis_policy_view ],
                [ p.partner_management_all ],
                [ p.partner_management_partners_all ],
                [ p.partner_management_partners_view ],
                [ p.partner_management_partners_create ],
                [ p.partner_management_partners_update ],
                [ p.partner_management_partners_delete ],
              ],
            },
            {
              id: 'customers',
              title: t( 'menu.item.customers' ),
              type: 'item',
              url: '/customers',
              breadcrumbs: false,
              requiredPermissions: [
                [ p.eis_policy_view ],
                [ p.partner_management_all ],
                [ p.partner_management_partners_all ],
                [ p.partner_management_partners_view ],
                [ p.partner_management_partners_create ],
                [ p.partner_management_partners_update ],
                [ p.partner_management_partners_delete ],
              ],
            },
            {
              id: 'provisions',
              title: t( 'menu.item.partnersProvisions' ),
              type: 'item',
              url: '/partners/commission-agreements',
              breadcrumbs: false,
              nestedApplication: true,
              requiredPermissions: [
                [ p.eis_policy_view ],
                [ p.commission_management_all ],
                [ p.commission_management_agreements_view ],
                [ p.commission_management_agreements_create ],
                [ p.commission_management_agreements_update ],
                [ p.commission_management_agreements_delete ],
              ],
            },
          ],
        },
        {
          id: 'policies',
          title: t( 'menu.item.policies' ),
          type: 'collapse',
          icon: 'feather icon-box',
          url: '/',
          breadcrumbs: false,
          requiredPermissions: [
            [ p.eis_policy_view ],
            [ p.eis_policy_create ],
            [ p.eis_leads_view ],
            [ p.eis_leads_create ],
            [ p.eis_leads_all ],
            [ p.policy_management_policies_view ],
            [ p.policy_management_policies_create ],
            [ p.policy_management_policies_update ],
            [ p.policy_management_policies_delete ],
            [ p.policy_management_leads_view ],
            [ p.policy_management_leads_create ],
            [ p.policy_management_leads_update ],
            [ p.policy_management_leads_delete ],
          ],
          ids: [ 'policies', 'leads' ],
          children:[
            {
              id: 'leads',
              title: t( 'menu.item.leads' ),
              type: 'item',
              url: '/leads',
              breadcrumbs: false,
              requiredPermissions: [
                [ p.eis_policy_view ],
                [ p.eis_policy_create ],
                [ p.eis_leads_view ],
                [ p.eis_leads_create ],
                [ p.eis_leads_all ],
                [ p.policy_management_policies_view ],
                [ p.policy_management_policies_create ],
                [ p.policy_management_policies_update ],
                [ p.policy_management_policies_delete ],
                [ p.policy_management_leads_view ],
                [ p.policy_management_leads_create ],
                [ p.policy_management_leads_update ],
                [ p.policy_management_leads_delete ],
              ],
            },
            {
              id: 'contracts',
              title: t( 'menu.item.contracts' ),
              type: 'item',
              url: '/policies',
              breadcrumbs: false,
              requiredPermissions: [
                [ p.eis_policy_view ],
                [ p.eis_policy_create ],
                [ p.eis_leads_view ],
                [ p.eis_leads_create ],
                [ p.eis_leads_all ],
                [ p.policy_management_policies_view ],
                [ p.policy_management_policies_create ],
                [ p.policy_management_policies_update ],
                [ p.policy_management_policies_delete ],
                [ p.policy_management_leads_view ],
                [ p.policy_management_leads_create ],
                [ p.policy_management_leads_update ],
                [ p.policy_management_leads_delete ],
              ],
            },
          ],
        },
        {
          id: 'motor',
          title: t( 'menu.item.motor' ),
          type: 'collapse',
          icon:'feather icon-truck',
          url: '/motor',
          breadcrumbs: true,
          requiredPermissions: [
            [ p.demo_view ],
            [ p.gdv_view ],
            [ p.gdv_view_dot ],
            [ p.gdv_all ],
            [ p.gdv_management_operations_all ],
            [ p.gdv_management_operations_view ],
            [ p.gdv_management_operations_create ],
            [ p.gdv_management_operations_update ],
            [ p.gdv_management_operations_delete ],
          ],
          children: [
            {
              id: 'fzvInboxList',
              title: t( 'menu.item.gdvList' ),
              type: 'item',
              url: '/motor/gdv',
              breadcrumbs: false,
              nestedApplication: true,
              requiredPermissions: [
                [ p.gdv_view ],
                [ p.gdv_view_dot ],
                [ p.gdv_all ],
                [ p.gdv_management_operations_all ],
                [ p.gdv_management_operations_view ],
                [ p.gdv_management_operations_create ],
                [ p.gdv_management_operations_update ],
                [ p.gdv_management_operations_delete ],
              ],
            },
          ],
        },
        {
          id: 'claims',
          title: t( 'menu.item.claims' ),
          type: 'item',
          icon: 'feather icon-flag',
          url: '/claims',
          breadcrumbs: false,
          requiredPermissions: [
            [ p.eis_claims_view ],
            [ p.eis_claims_create ],
            [ p.eis_claims_update ],
            [ p.eis_claims_delete ],
            [ p.eis_claims_all ],
            [ p.claim_management_claims_view ],
            [ p.claim_management_claims_create ],
            [ p.claim_management_claims_update ],
            [ p.claim_management_claims_delete ],
            [ p.claim_management_all ],
            [ p.claim_management_claims_all ],
          ],
        },
        {
          id: 'finance-reports',
          title: t( 'menu.item.reports' ),
          type: 'item',
          icon: 'feather icon-pie-chart',
          breadcrumbs: false,
          url: '/finance-reports',
          nestedApplication: true,
          requiredPermissions: [ p.reporting_dashboards_view ],
          forbiddenPermissions: [ p.eis_finance_dashboards_view ],
        },
        {
          id: 'management',
          title: t( 'menu.item.management' ),
          type: 'collapse',
          icon: 'feather icon-layers',
          url: '/management',
          breadcrumbs: true,
          // This means OR operation
          requiredPermissions: [
            [ p.demo_view ],
            [ p.eis_settings_view ],
            [ p.eis_products_view ],
            [ p.eis_products_update ],
            [ p.tenant_management_organizations_update ],
            [ p.tenant_management_users_update ],
            [ p.policy_management_products_view ],
            [ p.tenants_users_update ],
            [ p.tenants_users_all ],
            [ p.tenants_roles_update ],
            [ p.tenants_roles_all ],
            [ p.tenant_management_all ],
            [ p.tenant_management_organizations_all ],
            [ p.tenant_management_users_all ],
            [ p.identity_management_roles_update ],
            [ p.identity_management_roles_all ],
            [ p.process_management_workflows_view ],
            [ p.process_management_workflows_update ],
            [ p.payment_management_bank_accounts_update ],
          ],
          children: [
            {
              id: 'm-dashboard',
              title: t( 'menu.item.dashboard' ),
              type: 'item',
              url: '/management/dashboard',
              requiredPermissions: [ p.demo_view ],
            },
            {
              id: 'general-settings',
              title: t( 'menu.item.generalSettings' ),
              type: 'item',
              url: '/management/general-settings',
              breadcrumbs: false,
              requiredPermissions: [
                [ p.eis_settings_view ],
                [ p.tenant_management_organizations_update ],
                [ p.tenant_management_users_update ],
                [ p.tenants_users_update ],
                [ p.tenants_users_all ],
                [ p.tenants_roles_update ],
                [ p.tenants_roles_all ],
                [ p.tenant_management_all ],
                [ p.tenant_management_organizations_all ],
                [ p.tenant_management_users_all ],
                [ p.identity_management_roles_update ],
                [ p.identity_management_roles_all ],
                [ p.process_management_workflows_view ],
                [ p.process_management_workflows_update ],
                [ p.payment_management_bank_accounts_update ],
              ],
            },
            {
              id: 'product-settings',
              title: t( 'menu.item.productSettings' ),
              type: 'item',
              url: '/management/product-settings',
              breadcrumbs: false,
              requiredPermissions: [
                [ p.eis_products_view ],
                [ p.eis_products_create ],
                [ p.policy_management_products_view ],
              ],
            },
          ],
        },
        {
          id: 'finance-management',
          title: t( 'menu.item.financeDashboard' ),
          type: 'collapse',
          icon: 'feather icon-book-open',
          url: '/finance-management',
          breadcrumbs: false,
          requiredPermissions: [
            [ p.admin ],
            [ p.eis_finance ],
            [ p.feature_beta ],
            [ p.payment_management_bank_accounts_update ],
            [ p.payment_management_bank_orders_all ],
            [ p.reporting_dashboards_view ],
            [ p.billing_management_invoices_update ],
            [ p.payment_management_payments_view ],
            [ p.commission_management_all ],
            [ p.commission_management_agreements_view ],
            [ p.commission_management_agreements_create ],
            [ p.commission_management_agreements_update ],
            [ p.commission_management_agreements_delete ],
          ],
          children: [
            {
              id: 'finance-reporting',
              title: t( 'menu.item.financeReporting' ),
              type: 'item',
              breadcrumbs: false,
              url: '/finance-management/finance-report',
              requiredPermissions: [ p.eis_finance_dashboards_view ],
            },
            {
              id: 'finance-bank',
              title: t( 'menu.item.financeBank' ),
              type: 'item',
              url: '/bank/transactions',
              breadcrumbs: false,
              nestedApplication: true,
              requiredPermissions: [
                [ p.admin ],
                [ p.payment_management_bank_accounts_update ],
              ],
            },
            {
              id: 'finance-bank-orders',
              title: t( 'menu.item.financeBankOrders' ),
              type: 'item',
              url: '/bank/orders',
              breadcrumbs: false,
              nestedApplication: true,
              requiredPermissions: [
                [ p.admin ],
                [ p.payment_management_bank_orders_all ],
              ],
            },
            {
              id: 'financial-entities',
              title: t( 'menu.item.financialEntities' ),
              type: 'collapse',
              url: '/finance-management',
              breadcrumbs: false,
              requiredPermissions: [
                [ p.feature_beta ],
                [ p.admin ],
                [ p.billing_management_invoices_update ],
                [ p.payment_management_payments_view ],
                [ p.commission_management_all ],
                [ p.commission_management_agreements_view ],
                [ p.commission_management_agreements_create ],
                [ p.commission_management_agreements_update ],
                [ p.commission_management_agreements_delete ],
              ],
              children: [
                {
                  id: 'finance-invoices',
                  title: t( 'menu.item.financeInvoices' ),
                  type: 'item',
                  breadcrumbs: false,
                  url: '/finance-management/finance-invoices',
                  requiredPermissions: [
                    [ p.feature_beta ],
                    [ p.admin ],
                    [ p.billing_management_invoices_update ],
                  ],
                },
                {
                  id: 'payment-requests-claim-regulations',
                  title: t( 'menu.item.paymentRequestsClaimRegulation' ),
                  type: 'item',
                  breadcrumbs: false,
                  url: '/finance-management/payment-requests/claim-regulation',
                  nestedApplication: true,
                  requiredPermissions: [
                    [ p.admin ],
                    [ p.feature_beta ],
                    [ p.payment_management_payments_view ],
                  ],
                },
                {
                  id: 'payment-requests-refund-allocations',
                  title: t( 'menu.item.paymentRequestsRefundAllocation' ),
                  type: 'item',
                  breadcrumbs: false,
                  url: '/finance-management/payment-requests/refund-allocation',
                  nestedApplication: true,
                  requiredPermissions: [
                    [ p.admin ],
                    [ p.feature_beta ],
                    [ p.payment_management_payments_view ],
                  ],
                },
                {
                  id: 'commission-settlements',
                  title: t( 'menu.item.commissionSettlements' ),
                  type: 'item',
                  breadcrumbs: false,
                  url: '/partners/commissions',
                  nestedApplication: true,
                  requiredPermissions: [
                    [ p.commission_management_all ],
                    [ p.commission_management_agreements_view ],
                    [ p.commission_management_agreements_create ],
                    [ p.commission_management_agreements_update ],
                    [ p.commission_management_agreements_delete ],
                  ],
                },
              ],
            },
          ],
        },
        {
          id: 'editor',
          title:  t( 'menu.item.editor' ),
          type: 'collapse',
          icon: 'feather icon-file-plus',
          url: '/editor',
          breadcrumbs: false,
          requiredPermissions: [
            [ p.eis_products_view ],
            [ p.document_management_docx_templates_update ],
            [ p.document_management_templates_update ],
            [ p.document_management_documents_update ],
            [ p.notification_management_email_templates_update ],
          ],
          children: [
            {
              id: 'docx-editor',
              title: t( 'menu.item.docxEditor' ),
              type: 'item',
              url: '/editor/docx-documents',
              breadcrumbs: false,
              requiredPermissions: [
                [ p.eis_products_view ],
                [ p.document_management_docx_templates_update ],
              ],
            },
            {
              id: 'static-editor',
              title: t( 'menu.item.staticEditor' ),
              type: 'item',
              url: '/editor/static-documents',
              breadcrumbs: false,
              requiredPermissions: [
                [ p.eis_products_view ],
                [ p.document_management_documents_update ],
              ],
            },
            {
              id: 'document-editor',
              title: t( 'menu.item.documentEditor' ),
              type: 'item',
              url: '/editor/documents',
              breadcrumbs: false,
              requiredPermissions: [
                [ p.eis_products_view ],
                [ p.document_management_templates_update ],
              ],
            },
            {
              id: 'email-editor',
              title: t( 'menu.item.emailEditor' ),
              type: 'item',
              url: '/editor/emails',
              breadcrumbs: false,
              requiredPermissions: [
                [ p.eis_products_view ],
                [ p.notification_management_email_templates_update ],
              ],
            },
          ],
        },
      ],
    };
    const pbm: IAppProductDescriptor = {
      id: pbmID,
      name: 'Premium Benchmarking',
      menuItems: [
        {
          id: 'home',
          title: t( 'menu.item.home' ),
          type: 'item',
          icon: 'feather icon-home',
          url: '/premium-benchmarking/home',
          breadcrumbs: false,
          requiredPermissions: [ p.pbm_all ],
        },
        {
          id: 'dashboards',
          title: t( 'menu.item.dashboards' ),
          type: 'item',
          icon: 'feather icon-bar-chart-2',
          url: '/premium-benchmarking/dashboards',
          breadcrumbs: false,
          requiredPermissions: [ p.pbm_dashboard_view ],
        },
        {
          id: 'scenario-analyses',
          title: t( 'menu.item.scenarioAnalyses' ),
          type: 'item',
          icon: 'feather icon-activity',
          url: '/premium-benchmarking/scenario-analyses',
          breadcrumbs: false,
          requiredPermissions: [ p.pbm_scenarioanalyses_view ],
        },
        {
          id: 'tariffcomparisons',
          title: t( 'menu.item.tariffComparisons' ),
          type: 'item',
          icon: 'compare_arrows',
          iconType: 'material',
          url: '/premium-benchmarking/tariffcomparisons',
          breadcrumbs: false,
          requiredPermissions: [ p.pbm_tariffcomparisons_view ],
        },
        {
          id: 'alerts',
          title: t( 'menu.item.alerts' ),
          type: 'item',
          icon: 'feather icon-bell',
          url: '/premium-benchmarking/alerts',
          breadcrumbs: false,
          requiredPermissions: [ p.pbm_alerts_view ],
        },
        {
          id: 'reports',
          title: t( 'menu.item.reports' ),
          type: 'item',
          icon: 'feather icon-flag',
          url: '/premium-benchmarking/reports',
          breadcrumbs: false,
          requiredPermissions: [ p.pbm_reports_view ],
        },
        {
          id: 'settings',
          title: t( 'menu.item.settings' ),
          type: 'item',
          icon: 'feather icon-sliders',
          url: '/premium-benchmarking/general-settings',
          breadcrumbs: false,
          requiredPermissions: [ p.tenants_settings_all ],
        },
      ],
    };
    return [ eis, pbm ];
  }, [ t ] );

  return (
    <AppProductsContext.Provider value={ products }>
      { props.children }
    </AppProductsContext.Provider>
  );
};

export const AppProductsContext: React.Context<IAppProductDescriptor[]> = React.createContext( undefined as any );

export const useAppProducts = (): IAppProductDescriptor[] => {
  return React.useContext( AppProductsContext );
};

/**
 * It provides all products descriptors for current user by filtering all products by user permissions.
 */
export const AppUserProductsProvider: React.FC = ( props ) => {
  const products = useAppProducts();
  const user = useAppUser();
  const userProducts = React.useMemo<IAppProductDescriptor[]>( () => {
    return products.map( ( product ): IAppProductDescriptor => {
      const userMenuItems = product.menuItems.filter( ( menuItem ) => {
        if ( menuItem.children && menuItem.children.length ) {
          const items = menuItem.children.filter( ( childItem ) => {
            return user.hasPermissions( childItem.requiredPermissions, childItem.forbiddenPermissions );;
          } );

          menuItem.children = items;
        }

        return user.hasPermissions( menuItem.requiredPermissions, menuItem.forbiddenPermissions );
      } );

      return {
        ...product,
        menuItems: userMenuItems,
      };
    } );
  }, [ products, user ] );
  return (
    <AppUserProductsContext.Provider value={ userProducts }>
      { props.children }
    </AppUserProductsContext.Provider>
  );
};

export const AppUserProductsContext: React.Context<IAppProductDescriptor[]> = React.createContext( undefined as any );

export const useAppUserProducts = (): IAppProductDescriptor[] => {
  return React.useContext( AppUserProductsContext );
};

/**
 * Provides context of the current product selected. Product is defined in route descriptor.
 */
export const AppCurrentProductContext: React.Context<IAppProductDescriptor> = React.createContext( undefined as any );

export const useAppCurrentProduct = (): IAppProductDescriptor => {
  return React.useContext( AppCurrentProductContext );
};

export interface ISecurityContext {
  hasPermissions( permissions: PermissionType[] ): boolean;
}

export class DefaultSecurityContext implements ISecurityContext {
  private readonly hasUiPermission: boolean;
  private readonly finalPermissions: PermissionType[];
  constructor( userPermissions: PermissionType[] ) {
    // calculate common permissions
    this.finalPermissions = userPermissions;
    this.hasUiPermission = !!this.finalPermissions.find( ( permission ) => UI_VIEW_PERMISSIONS.includes( permission ) );
  }
  hasPermissions( requiredPermissions: PermissionType[] ): boolean {
    if ( !this.hasUiPermission ) {
      return false;
    }

    return hasRequiredPermissions( this.finalPermissions, requiredPermissions );
  }
}

export const AppSecurityProvider: React.FC = ( props ) => {
  const currentUser = useAppUser();
  const securityContext = React.useMemo<ISecurityContext>( () => {
    return new DefaultSecurityContext( currentUser.userPermissions );
  }, [ currentUser.userPermissions ] );
  return (
    <AppSecurityContext.Provider value={ securityContext }>
      { props.children }
    </AppSecurityContext.Provider>
  );
};

export const AppSecurityContext: React.Context<ISecurityContext> = React.createContext( undefined as any );

/**
 * Return current security context for current user and product
 */
export const useAppSecurity = (): ISecurityContext => {
  return React.useContext( AppSecurityContext );
};
